import type { IFramework } from '@/framework';
import type { Layer } from '@/layers/layer';
import type { ILayerProps } from '@/version-types';

export const deleteHierarchyLayer = (framework: IFramework, layerId: string) => {
    framework.service.registration.getInstance<Layer>('Layer').delete(layerId);
};

export const duplicateHierarchyLayer = (framework: IFramework, layerId: string) => {
    framework.service.registration.getInstance<Layer>('Layer').clone(layerId);
};

export const unlockHierarchyLayer = (framework: IFramework, layerId: string) => {
    framework.service.registration.getInstance<Layer>('Layer').unlock(layerId);
};

export const setHierarchyLayerHidden = (
    framework: IFramework,
    layerId: string,
    hidden: boolean,
    options: { syncCanvasVisibility?: boolean } = {},
) => {
    framework.service.registration.getInstance<Layer>('Layer').setHidden(layerId, hidden);
    if (options.syncCanvasVisibility) {
        framework.service.edgelessCanvas.getByLayerId(layerId)?.setVisible(!hidden);
    }
    return framework.service.flatLayer.getLayer(layerId);
};

export const setHierarchyLayerHovered = (framework: IFramework, layerId: string, hovered: boolean) => {
    if (hovered) {
        framework.service.frame.setHoverId(layerId);
    } else {
        framework.service.frame.delHoverId();
    }
};

export const sortHierarchyLayerInGroup = (
    framework: IFramework,
    layer: ILayerProps,
    sourceIndex: number,
    destinationIndex: number,
) => {
    if (!layer.parentId) return false;

    const groupLayer = framework.service.flatLayer.getLayer(layer.parentId);
    if (!groupLayer) return false;

    const previousChildren = groupLayer.children;
    const length = previousChildren.length;
    // 面板按绘制顺序倒序展示，DND 索引需转换回底层 children 的正序索引。
    const reversedSourceIndex = length - sourceIndex - 1;
    const reversedDestinationIndex = length - destinationIndex - 1;

    if (
        reversedSourceIndex < 0 ||
        reversedDestinationIndex < 0 ||
        reversedSourceIndex >= length ||
        reversedDestinationIndex >= length
    ) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn('[stage-toolbar/hierarchy] group sort index out of bounds');
        }
        return false;
    }

    const nextChildren = [...previousChildren];
    const [removed] = nextChildren.splice(reversedSourceIndex, 1);
    nextChildren.splice(reversedDestinationIndex, 0, removed);

    const nextGroupLayer = { ...groupLayer, children: nextChildren };
    framework.service.frame.addOrUpdateLayer(nextGroupLayer);
    framework.service.history.write(groupLayer, nextGroupLayer);
    return true;
};

export const sortHierarchyLayerInRoot = (
    framework: IFramework,
    options: {
        layer: ILayerProps;
        stageLayers: ILayerProps[];
        sourceIndex: number;
        destinationIndex: number;
        frameId: number;
    },
) => {
    const { layer, stageLayers, sourceIndex, destinationIndex, frameId } = options;
    const length = stageLayers.length;
    // 根图层列表同样倒序展示，排序服务接收的是底层正序偏移。
    const reversedSourceIndex = length - sourceIndex - 1;
    const reversedDestinationIndex = length - destinationIndex - 1;

    if (
        reversedSourceIndex < 0 ||
        reversedDestinationIndex < 0 ||
        reversedSourceIndex >= length ||
        reversedDestinationIndex >= length
    ) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn('[stage-toolbar/hierarchy] root sort index out of bounds');
        }
        return false;
    }

    const affectedLayers =
        reversedSourceIndex < reversedDestinationIndex
            ? stageLayers.slice(reversedSourceIndex + 1, reversedDestinationIndex + 1)
            : stageLayers.slice(reversedDestinationIndex);
    const sortList = affectedLayers.map(({ id }) => ({
        id,
        index: stageLayers.findIndex((stageLayer) => stageLayer.id === id),
    }));
    const childList = [
        {
            id: layer.id,
            index: stageLayers.findIndex((stageLayer) => stageLayer.id === layer.id),
        },
    ];

    framework.service.registration.getInstance<Layer>('Layer').sort(reversedDestinationIndex - reversedSourceIndex, {
        frameId,
        sortList,
        childList,
    });
    return true;
};
